import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogTitle, DialogContent, Box, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import capitalizeWords from '../../../../utils/capitalize';

const ConfirmModal = ({ onUpdate, updateSelected, onClose, action }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async (values) => {
    try {
      setIsUpdating(true);
      await onUpdate(values);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const validationSchema = Yup.object({
    holdStartDate: Yup.date().required('Hold start date is required').typeError('Please enter a valid date'),
    holdEndDate: Yup.date()
      .required('Hold end date is required')
      .typeError('Please enter a valid date')
      .test('is-after-start-date', 'End date must be after start date', function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { holdStartDate } = this.parent;
        return holdStartDate && value ? dayjs(value).isAfter(dayjs(holdStartDate)) : true;
      }),
  });

  const initialValues = {
    holdStartDate: null,
    holdEndDate: null,
  };

  return (
    <Dialog
      open={Boolean(updateSelected)}
      aria-labelledby="confirm-dialog-title"
      maxWidth="sm"
      fullWidth
      sx={{
        height: '85vh',
      }}
      PaperProps={{
        style: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle id="confirm-dialog-title">
        Are you sure you want to mark {action} "
        {updateSelected?.projectName ? capitalizeWords(updateSelected?.projectName) : ''}"?
      </DialogTitle>
      {action === 'hold' && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleUpdate}>
            {({ values, setFieldValue, touched, errors, isSubmitting }) => (
              <Form>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    <Box sx={{ width: '50%' }}>
                      <Field name="holdStartDate">
                        {({ field }) => (
                          <DatePicker
                            label="Hold Start Date"
                            value={values.holdStartDate}
                            onChange={(newValue) => setFieldValue('holdStartDate', newValue)}
                            inputFormat="DD MMM YYYY"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                {...field}
                                error={touched.holdStartDate && Boolean(errors.holdStartDate)}
                                helperText={touched.holdStartDate && errors.holdStartDate}
                                fullWidth
                              />
                            )}
                          />
                        )}
                      </Field>
                    </Box>
                    <Box sx={{ width: '50%' }}>
                      <Field name="holdEndDate">
                        {({ field }) => (
                          <DatePicker
                            label="Hold End Date"
                            value={values.holdEndDate}
                            onChange={(newValue) => setFieldValue('holdEndDate', newValue)}
                            inputFormat="DD MMM YYYY"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                {...field}
                                error={touched.holdEndDate && Boolean(errors.holdEndDate)}
                                helperText={touched.holdEndDate && errors.holdEndDate}
                                fullWidth
                              />
                            )}
                          />
                        )}
                      </Field>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button disabled={isUpdating || isSubmitting} onClick={onClose}>
                    Cancel
                  </Button>
                  <LoadingButton type="submit" loading={isUpdating || isSubmitting}>
                    Confirm
                  </LoadingButton>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </LocalizationProvider>
      )}
      {action !== 'hold' && (
        <DialogActions>
          <Button disabled={isUpdating} onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton loading={isUpdating} onClick={handleUpdate}>
            Confirm
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

ConfirmModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  updateSelected: PropTypes.object,
  action: PropTypes.string.isRequired,
};

export default ConfirmModal;
